import { Injectable, inject } from '@angular/core';
import {
  ConvertService,
  type ImageRequest,
} from '@imagise/generated-imagise-cloud-api-client';
import { FileResult } from '@imagise/entities';
import { ObjectUrlHolder } from '@imagise/shared';
import { Observable, map } from 'rxjs';
import { EditorPageData } from '../domain/interfaces/editor-data';
import { AsyncOperationStatus } from '../domain/interfaces/responses';
import {
  buildOutputFileName,
  normalizeDestinationFormat,
} from '../domain/output-format.utils';
import { checkOperationResponse } from '../../error-reporting/operation-request';

@Injectable({
  providedIn: 'root',
})
export class EditorService {
  #convertService = inject(ConvertService);

  private readonly downloadUrl = new ObjectUrlHolder();

  applyEditorChanges(
    editorData: EditorPageData[],
    destFormat: string,
    fileName: string,
    sourceFile: FileResult
  ): Observable<AsyncOperationStatus<FileResult>> {
    const imageBase64Files = this.resolveSourceImages(editorData, sourceFile);
    const body: ImageRequest = {
      toFormat: normalizeDestinationFormat(destFormat),
      imageBase64Files,
    };

    return this.#convertService.convert(body).pipe(
      checkOperationResponse(),
      map((resultBlob) => {
        const downloadUrl = this.downloadUrl.create(resultBlob);
        const outputFileName = buildOutputFileName(fileName, destFormat);

        const payload: FileResult = {
          IsSuccess: true,
          idError: '',
          id: sourceFile.id,
          FileName: outputFileName,
          UploadFileName: downloadUrl,
          idUpload: sourceFile.idUpload,
          FileLength: resultBlob.size,
          SourceBase64: sourceFile.SourceBase64,
          SourceMimeType: resultBlob.type || sourceFile.SourceMimeType,
          LocalObjectUrl: sourceFile.LocalObjectUrl,
          DownloadUrl: downloadUrl,
          FileBlob: resultBlob,
        };

        const status: AsyncOperationStatus<FileResult> = {
          OperationId: sourceFile.id || 'editor-convert',
          Progress: 100,
          State: 'Completed',
          Payload: payload,
        };

        return status;
      })
    );
  }

  private resolveSourceImages(
    editorData: EditorPageData[],
    sourceFile: FileResult
  ): string[] {
    const editedPagesBase64 = editorData
      .map((page) => page.Data?.trim())
      .filter((svgData): svgData is string => Boolean(svgData))
      .map((svgData) => this.encodeUtf8ToBase64(svgData));

    if (editedPagesBase64.length > 0) {
      return editedPagesBase64;
    }

    if (sourceFile.SourceBase64) {
      return [sourceFile.SourceBase64];
    }

    throw new Error('Source image is missing for conversion.');
  }

  private encodeUtf8ToBase64(value: string): string {
    if (typeof btoa !== 'function') {
      throw new Error('Base64 encoder is not available in this runtime.');
    }

    const utf8Value = encodeURIComponent(value).replace(
      /%([0-9A-F]{2})/g,
      (_: string, hexCode: string) =>
        String.fromCharCode(Number.parseInt(hexCode, 16))
    );

    return btoa(utf8Value);
  }
}
